import React, { useState, useEffect } from 'react';
import { whatsappAPI } from '../../services/api';
import { 
  ShoppingCart, 
  Package, 
  FileText, 
  Settings, 
  Send, 
  Plus, 
  Edit, 
  Trash2, 
  BarChart3,
  ToggleLeft,
  ToggleRight,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';

const WhatsAppEcommerce = () => {
  const [activeTab, setActiveTab] = useState('abandoned-carts');
  const [settings, setSettings] = useState({});
  const [abandonedCarts, setAbandonedCarts] = useState([]);
  const [orderNotifications, setOrderNotifications] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Form states
  const [cartForm, setCartForm] = useState({
    userId: '',
    phoneNumber: '',
    cartData: '',
    totalAmount: ''
  });

  const [notificationForm, setNotificationForm] = useState({
    orderId: '',
    phoneNumber: '',
    notificationType: 'order_confirmed',
    trackingData: {}
  });

  const [catalogForm, setCatalogForm] = useState({
    productId: '',
    title: '',
    description: '',
    price: '',
    currency: 'INR',
    imageUrl: '',
    category: ''
  });

  useEffect(() => {
    loadSettings();
    loadData();
  }, [activeTab]);

  const loadSettings = async () => {
    try {
      console.log('🔍 Frontend: Loading settings...');
      const response = await whatsappAPI.getEcommerceSettings();
      console.log('🔍 Frontend: Settings response:', response);
      
      if (response.data.success) {
        console.log('🔍 Frontend: Setting settings state:', response.data.data);
        setSettings(response.data.data);
      } else {
        console.error('🔍 Frontend: Failed to load settings:', response.data);
      }
    } catch (error) {
      console.error('❌ Frontend: Error loading settings:', error);
    }
  };


  const loadData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'abandoned-carts':
          const cartsResponse = await whatsappAPI.getAbandonedCarts();
          if (cartsResponse.data.success) {
            setAbandonedCarts(cartsResponse.data.data || []);
          }
          break;
        case 'order-notifications':
          const notificationsResponse = await whatsappAPI.getOrderNotifications();
          if (notificationsResponse.data.success) {
            setOrderNotifications(notificationsResponse.data.data || []);
          }
          break;
        case 'catalog':
          const catalogResponse = await whatsappAPI.getCatalogItems();
          if (catalogResponse.data.success) {
            setCatalogItems(catalogResponse.data.data || []);
          }
          break;
        case 'analytics':
          const analyticsResponse = await whatsappAPI.getEcommerceAnalytics();
          if (analyticsResponse.data.success) {
            // Handle analytics data
          }
          break;
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key, value) => {
    try {
      console.log('🔍 Frontend: Updating setting:', { key, value });
      
      // Send the data in the correct format: { setting_key: setting_value }
      const requestData = { [key]: value };
      console.log('🔍 Frontend: Sending request data:', requestData);
      
      const response = await whatsappAPI.updateEcommerceSettings(requestData);
      console.log('🔍 Frontend: Response received:', response);
      
      if (response.data.success) {
        setMessage('Setting updated successfully');
        console.log('🔍 Frontend: Reloading settings...');
        // Reload settings to reflect the change
        await loadSettings();
        console.log('🔍 Frontend: Settings reloaded');
        
        // Clear success message after 3 seconds
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Failed to update setting');
        console.error('Failed to update setting:', response.data);
        
        // Clear error message after 5 seconds
        setTimeout(() => setMessage(''), 5000);
      }
    } catch (error) {
      setMessage('Error updating setting');
      console.error('Error updating setting:', error);
    }
  };

  const trackAbandonedCart = async (e) => {
    e.preventDefault();
    try {
      let cartData;
      try {
        cartData = JSON.parse(cartForm.cartData);
      } catch (jsonError) {
        setMessage('Invalid JSON format in cart data. Please use valid JSON format like: {"items": [{"name": "Product", "price": 100}]}');
        return;
      }

      if (!cartForm.phoneNumber) {
        setMessage('Phone number is required');
        return;
      }

      if (!cartForm.totalAmount || isNaN(parseFloat(cartForm.totalAmount))) {
        setMessage('Valid total amount is required');
        return;
      }

      const response = await whatsappAPI.trackAbandonedCart({
        userId: cartForm.userId || null,
        phoneNumber: cartForm.phoneNumber,
        cartData: cartData,
        totalAmount: parseFloat(cartForm.totalAmount)
      });
      
      if (response.data.success) {
        setMessage('Cart tracked successfully');
        setCartForm({ userId: '', phoneNumber: '', cartData: '', totalAmount: '' });
      } else {
        setMessage('Error tracking cart');
      }
    } catch (error) {
      setMessage('Error tracking cart');
      console.error('Error tracking cart:', error);
    }
  };

  const sendOrderNotification = async (e) => {
    e.preventDefault();
    try {
      if (!notificationForm.orderId) {
        setMessage('Order ID is required');
        return;
      }

      if (!notificationForm.phoneNumber) {
        setMessage('Phone number is required');
        return;
      }

      const response = await whatsappAPI.sendOrderNotification({
        orderId: notificationForm.orderId,
        phoneNumber: notificationForm.phoneNumber,
        notificationType: notificationForm.notificationType,
        trackingData: notificationForm.trackingData || {}
      });
      
      if (response.data.success) {
        setMessage('Order notification sent successfully');
        setNotificationForm({
          orderId: '',
          phoneNumber: '',
          notificationType: 'order_confirmed',
          trackingData: {}
        });
      } else {
        setMessage('Error sending order notification');
      }
    } catch (error) {
      setMessage('Error sending order notification');
      console.error('Error sending order notification:', error);
    }
  };

  const syncCatalogItem = async (e) => {
    e.preventDefault();
    try {
      const catalogData = {
        productId: catalogForm.productId,
        title: catalogForm.title,
        description: catalogForm.description,
        price: parseFloat(catalogForm.price),
        currency: catalogForm.currency,
        imageUrl: catalogForm.imageUrl,
        category: catalogForm.category
      };

      const response = await whatsappAPI.syncCatalogItem(catalogData);
      
      if (response.data.success) {
        setMessage('Catalog item synced successfully');
        setCatalogForm({
          productId: '',
          title: '',
          description: '',
          price: '',
          currency: 'INR',
          imageUrl: '',
          category: ''
        });
      } else {
        setMessage('Error syncing catalog item');
      }
    } catch (error) {
      setMessage('Error syncing catalog item');
      console.error('Error syncing catalog item:', error);
    }
  };

  const tabs = [
    { id: 'abandoned-carts', label: 'Abandoned Carts', icon: ShoppingCart },
    { id: 'order-notifications', label: 'Order Notifications', icon: Package },
    { id: 'catalog', label: 'Catalog Management', icon: FileText },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  const notificationTypes = [
    { value: 'order_confirmed', label: 'Order Confirmed' },
    { value: 'payment_received', label: 'Payment Received' },
    { value: 'processing', label: 'Processing' },
    { value: 'shipped', label: 'Shipped' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  return (
    <div className="p-4 sm:p-6 bg-white dark:bg-gray-900 rounded-lg shadow-lg">
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-2">
          WhatsApp E-Commerce Integration
        </h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
          Manage abandoned cart reminders, order notifications, and catalog integration
        </p>
      </div>

      {message && (
        <div className={`mb-4 p-4 rounded-lg ${
          message.includes('Error') 
            ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' 
            : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
        }`}>
          {message}
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex overflow-x-auto scrollbar-hide">
          <div className="flex space-x-2 sm:space-x-8 min-w-max">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-1 sm:space-x-2 py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <Icon size={16} />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                </button>
              );
            })}
          </div>
        </nav>
      </div>

      {/* Content */}
      <div className="space-y-6">
        {activeTab === 'abandoned-carts' && (
          <div className="space-y-6">
            <div className="bg-gray-50 dark:bg-gray-800 p-4 sm:p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Track Abandoned Cart
              </h3>
              <form onSubmit={trackAbandonedCart} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      User ID
                    </label>
                    <input
                      type="number"
                      value={cartForm.userId}
                      onChange={(e) => setCartForm({...cartForm, userId: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="User ID (optional)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="text"
                      value={cartForm.phoneNumber}
                      onChange={(e) => setCartForm({...cartForm, phoneNumber: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="+1234567890"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Cart Data (JSON)
                  </label>
                  <textarea
                    value={cartForm.cartData}
                    onChange={(e) => setCartForm({...cartForm, cartData: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    rows="4"
                    placeholder='{"items": [{"name": "Product", "price": 100}]}'
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Total Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={cartForm.totalAmount}
                    onChange={(e) => setCartForm({...cartForm, totalAmount: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="99.99"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center space-x-2"
                >
                  <Send size={16} />
                  <span>Track Cart</span>
                </button>
              </form>
            </div>
          </div>
        )}

        {activeTab === 'order-notifications' && (
          <div className="space-y-6">
            <div className="bg-gray-50 dark:bg-gray-800 p-4 sm:p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Send Order Notification
              </h3>
              <form onSubmit={sendOrderNotification} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Order ID
                    </label>
                    <input
                      type="text"
                      value={notificationForm.orderId}
                      onChange={(e) => setNotificationForm({...notificationForm, orderId: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="ORDER123"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="text"
                      value={notificationForm.phoneNumber}
                      onChange={(e) => setNotificationForm({...notificationForm, phoneNumber: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="+1234567890"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Notification Type
                  </label>
                  <select
                    value={notificationForm.notificationType}
                    onChange={(e) => setNotificationForm({...notificationForm, notificationType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    {notificationTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="submit"
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center space-x-2"
                >
                  <Send size={16} />
                  <span>Send Notification</span>
                </button>
              </form>
            </div>
          </div>
        )}

        {activeTab === 'catalog' && (
          <div className="space-y-6">
            <div className="bg-gray-50 dark:bg-gray-800 p-4 sm:p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Sync Catalog Item
              </h3>
              <form onSubmit={syncCatalogItem} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Product ID
                    </label>
                    <input
                      type="text"
                      value={catalogForm.productId}
                      onChange={(e) => setCatalogForm({...catalogForm, productId: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="PROD123"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Title
                    </label>
                    <input
                      type="text"
                      value={catalogForm.title}
                      onChange={(e) => setCatalogForm({...catalogForm, title: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Product Title"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={catalogForm.description}
                    onChange={(e) => setCatalogForm({...catalogForm, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    rows="3"
                    placeholder="Product description..."
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Price
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={catalogForm.price}
                      onChange={(e) => setCatalogForm({...catalogForm, price: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="99.99"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Currency
                    </label>
                    <select
                      value={catalogForm.currency}
                      onChange={(e) => setCatalogForm({...catalogForm, currency: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="INR">INR</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Category
                    </label>
                    <input
                      type="text"
                      value={catalogForm.category}
                      onChange={(e) => setCatalogForm({...catalogForm, category: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Electronics"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Image URL
                  </label>
                  <input
                    type="url"
                    value={catalogForm.imageUrl}
                    onChange={(e) => setCatalogForm({...catalogForm, imageUrl: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                <button
                  type="submit"
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md flex items-center space-x-2"
                >
                  <Plus size={16} />
                  <span>Sync Catalog Item</span>
                </button>
              </form>
            </div>
          </div>
        )}


        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:p-6">
              <div className="bg-blue-50 dark:bg-blue-900 p-4 sm:p-6 rounded-lg">
                <div className="flex items-center">
                  <ShoppingCart className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Total Carts</p>
                    <p className="text-xl sm:text-2xl font-bold text-blue-900 dark:text-blue-100">0</p>
                  </div>
                </div>
              </div>
              <div className="bg-green-50 dark:bg-green-900 p-4 sm:p-6 rounded-lg">
                <div className="flex items-center">
                  <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-green-600 dark:text-green-400">Converted</p>
                    <p className="text-xl sm:text-2xl font-bold text-green-900 dark:text-blue-100">0</p>
                  </div>
                </div>
              </div>
              <div className="bg-yellow-50 dark:bg-yellow-900 p-4 sm:p-6 rounded-lg">
                <div className="flex items-center">
                  <Clock className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-yellow-600 dark:text-yellow-400">Active</p>
                    <p className="text-xl sm:text-2xl font-bold text-yellow-900 dark:text-yellow-100">0</p>
                  </div>
                </div>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900 p-4 sm:p-6 rounded-lg">
                <div className="flex items-center">
                  <BarChart3 className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-purple-600 dark:text-purple-400">Avg Value</p>
                    <p className="text-xl sm:text-2xl font-bold text-purple-900 dark:text-purple-100">₹0</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div className="bg-gray-50 dark:bg-gray-800 p-4 sm:p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                E-Commerce Settings
              </h3>
              <div className="space-y-4">
                {Object.entries(settings).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between p-4 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Current: {value}
                      </p>
                    </div>
                    <button
                      onClick={() => updateSetting(key, value === 'true' ? 'false' : 'true')}
                      className={`flex items-center space-x-2 px-3 py-1 rounded-md text-sm font-medium ${
                        value === 'true'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      }`}
                    >
                      {value === 'true' ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                      <span>{value === 'true' ? 'Enabled' : 'Disabled'}</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppEcommerce;
